<script setup lang="ts">
// The single centered-dialog shell for the whole web-ui. Now a thin delegate to
// the shared @hq/chat-ui Dialog (Reka), so every modal that already imports this
// file (Prompt / Confirm / Invite / AvatarGenerate / AddWorkspace ...) gains
// real focus-trap, scroll-lock, return-focus and aria wiring with zero call-site
// changes - the props/slots/`close` event match what they passed before.
//
// Body content is the default slot; footer buttons go in #footer; a fully
// custom header goes in #header. Side panels (ApiKeyConnectPanel /
// ScopePickerPanel) are a different pattern (slide-in sheet) and don't use this.
import { computed } from 'vue';
import { Dialog } from '@hq/chat-ui';
import { iconComp } from '@/lib/icon-component';
import type { IconName } from '@/lib/icons';

// Optionals are explicitly `| undefined` so callers can bind optional-chained
// values (e.g. `:title="req?.title"`) under exactOptionalPropertyTypes.
const props = withDefaults(defineProps<{
  open: boolean;
  title?: string | undefined;
  /** Optional leading header icon. */
  icon?: IconName | undefined;
  /** While busy, backdrop click / Escape / the close button are inert. */
  busy?: boolean | undefined;
  size?: 'sm' | 'md' | 'lg' | undefined;
  /** Caller controls dismissal: backdrop + Escape do not close. */
  persistent?: boolean | undefined;
  /** Hide the header close (X). */
  hideClose?: boolean | undefined;
  /** `alertdialog` for destructive / must-acknowledge dialogs. */
  role?: 'dialog' | 'alertdialog' | undefined;
  /** Tone the header icon as destructive (red) instead of the accent. */
  danger?: boolean | undefined;
}>(), {
  busy: false,
  size: 'md',
  persistent: false,
  hideClose: false,
  role: 'dialog',
  danger: false,
});

const emit = defineEmits<{ (e: 'close'): void }>();

// chat-ui's Dialog takes the header icon as a Component; adapt our IconName.
const dialogProps = computed(() => ({
  ...props,
  icon: props.icon ? iconComp(props.icon) : undefined,
}));
</script>

<template>
  <Dialog v-bind="dialogProps" @close="emit('close')">
    <template v-if="$slots.header" #header><slot name="header" /></template>
    <slot />
    <template v-if="$slots.footer" #footer><slot name="footer" /></template>
  </Dialog>
</template>
